// MultiDocumentUpload.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  useForm,
  useFieldArray,
  FormProvider,
  Controller,
} from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import JSZip from "jszip";
import {
  Box,
  Button,
  Card,
  CardHeader,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  CLIENT_DOC_CATEGORIES,
  NON_CLIENT_DOC_CATEGORIES,
  CLIENT_ACCESS_LEVEL,
  FIRM_BUSINESS_OBJECT_ENUM,
  INVESTMENT_BUSINESS_OBJECT_ENUM,
  COMPANY_BUSINESS_OBJECT_ENUM,
  DEAL_BUSINESS_OBJECT_ENUM,
  FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
  ORGANIZATION_ACCESS_LEVEL,
  getSiriusBaseUrl,
} from "./constants";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useSearchParams } from "react-router-dom";

import { Link, List, ListItem, ListItemText } from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker as DatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import DocumentRowForm from "./documentRowForm";
import DocumentEntityInput from "./documentEntityInput";
import {
  ADD_DOCUMENT_QUEUE_SIGNED_URI,
  ADD_KNOWLEDGE_BASE_TAG,
  GET_DOCUMENT_ACCESS_LEVELS,
  GET_DOCUMENT_TYPE_ENUMS,
  GET_DOCUMENT_QUEUES_BY_IDS,
  PROCESS_DOCUMENT_QUEUE,
  PUBLISH_DOCUMENT_QUEUE,
  DELETE_DOCUMENT_QUEUE,
  GET_DOCUMENT_AUTH_GROUPS,
} from "./graphql";
import {
  FormValues,
  DocumentQueue,
  DocumentQueueFormInput,
  EntityAssociation,
} from "./types";
import { getDateValue } from "./utils";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SendIcon from "@mui/icons-material/Send";
import { useCurrentUser } from "../../utils/hooks";
import { GetDocumentAuthGroupsQuery } from "_graphql-types-frontend/graphql";

interface PublishedDocument {
  id: number;
  name: string;
}

/**
 * Extract EntityAssociation[] from the form's entities multi-select structure.
 * Each entity type selector stores an array of {id, name} objects keyed by businessObjectEnumId.
 */
export function extractEntitiesFromFormData(
  entitiesMap: Record<number, Array<{ id: number; name?: string }>> | undefined
): EntityAssociation[] {
  if (!entitiesMap) return [];

  const result: EntityAssociation[] = [];
  const entityTypes = [
    INVESTMENT_BUSINESS_OBJECT_ENUM,
    FIRM_BUSINESS_OBJECT_ENUM,
    COMPANY_BUSINESS_OBJECT_ENUM,
    DEAL_BUSINESS_OBJECT_ENUM,
    FIRM_ENGAGEMENT_BUSINESS_OBJECT_ENUM,
  ];

  for (const businessObjectEnumId of entityTypes) {
    const selected = entitiesMap[businessObjectEnumId];
    if (Array.isArray(selected)) {
      for (const entity of selected) {
        if (entity?.id) {
          result.push({
            businessObjectEnumId,
            entityId: entity.id,
            entityName: entity.name,
          });
        }
      }
    }
  }

  return result;
}

const MultiDocumentUpload: React.FC = () => {
  const { organizationId } = useCurrentUser();
  const [publishedDocuments, setPublishedDocuments] = useState<
    Array<PublishedDocument>
  >([]);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: "success" | "error" | "info";
    open: boolean;
  }>({ message: "", type: "info", open: false });

  const [authGroup, setAuthGroup] = useState<
    GetDocumentAuthGroupsQuery["userList"]["items"][0] | null
  >(null);

  // Get business object enum from prior route.
  const [query] = useSearchParams();

  // Form methods
  const methods = useForm<FormValues>({
    defaultValues: {
      queues: [],
      overrideAccessLevel: undefined,
      overrideDocumentTypeEnumId: undefined,
      overrideDate: undefined,
      overrideEntities: undefined,
    },
  });

  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>(
    {}
  );

  // Track which fields were auto-populated by LLM
  const [autoPopulatedFields, setAutoPopulatedFields] = useState<string[]>([]);

  // Track which documents are being processed by AI
  const [processingDocuments, setProcessingDocuments] = useState<Set<string>>(
    new Set()
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    resetField,
    getValues,
    trigger,
    formState: { errors, touchedFields },
  } = methods;
  const { fields } = useFieldArray({
    control,
    name: "queues",
  });

  const previousOverrideAccessLevelRef = useRef<number | undefined>();

  const overrideAccessLevel = watch("overrideAccessLevel");

  useEffect(() => {
    const previous = previousOverrideAccessLevelRef.current;
    if (overrideAccessLevel === 4 || previous === 4) {
      resetField("overrideDocumentTypeEnumId");
    }
    previousOverrideAccessLevelRef.current = overrideAccessLevel;
  }, [overrideAccessLevel]);

  // Helper function to extract validation error messages
  const getValidationErrors = () => {
    const errorMessages: string[] = [];

    const extractErrors = (obj: any, path: string = ""): void => {
      if (obj?.message && typeof obj.message === "string") {
        errorMessages.push(`${path}: ${obj.message}`);
        return;
      }

      if (obj && typeof obj === "object") {
        Object.entries(obj).forEach(([key, value]) => {
          const newPath = path ? `${path}.${key}` : key;
          extractErrors(value, newPath);
        });
      }
    };

    extractErrors(errors);
    return [...new Set(errorMessages)]; // Remove duplicates
  };

  // GraphQL queries and mutations
  const { data: accessLevelsData } = useQuery(GET_DOCUMENT_ACCESS_LEVELS);

  const { data: documentTypesData } = useQuery(GET_DOCUMENT_TYPE_ENUMS, {
    variables: {
      filter: {
        documentCategoryEnumIds:
          overrideAccessLevel === CLIENT_ACCESS_LEVEL
            ? CLIENT_DOC_CATEGORIES
            : NON_CLIENT_DOC_CATEGORIES,
        isBusinessApplicantType: false,
      },
    },
  });

  const { data: documentAuthGroupsData } = useQuery(GET_DOCUMENT_AUTH_GROUPS, {
    variables: { organizationId: organizationId! },
    onCompleted: data => {
      if (data.userList.items.length > 0) {
        setAuthGroup(data.userList?.items[0] || null);
      }
    },
  });

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileUpload(Array.from(event.target.files));
      // Clear the input value so the same file can be uploaded again after removal
      event.target.value = "";
    }
  };

  const [addDocumentQueueSignedURI] = useMutation(
    ADD_DOCUMENT_QUEUE_SIGNED_URI
  );
  const [processDocumentQueue] = useMutation(PROCESS_DOCUMENT_QUEUE);
  const [publishDocumentQueue] = useMutation(PUBLISH_DOCUMENT_QUEUE);
  const [addKnowledgeBaseTag] = useMutation(ADD_KNOWLEDGE_BASE_TAG);
  const [deleteDocumentQueue] = useMutation(DELETE_DOCUMENT_QUEUE);

  // State to track document queue IDs for polling
  const [documentQueueIds, setDocumentQueueIds] = useState<string[]>([]);

  // Polling query for document queue LLM results
  const {
    data: documentQueuesData,
    startPolling,
    stopPolling,
  } = useQuery(GET_DOCUMENT_QUEUES_BY_IDS, {
    variables: { ids: documentQueueIds },
    skip: documentQueueIds.length === 0,
    pollInterval: 2000, // Poll every 2 seconds
    fetchPolicy: "cache-and-network",
  });

  const queues = watch("queues");

  // Handle LLM results from polling
  useEffect(() => {
    if (documentQueuesData?.documentQueueMany) {
      documentQueuesData.documentQueueMany.forEach(docQueue => {
        if (!docQueue) return;

        // Find the corresponding form field index
        const queueIndex = queues.findIndex(
          (queue: DocumentQueueFormInput) =>
            queue.documentQueueId === docQueue.id
        );

        if (queueIndex === -1) return;

        // Update form fields with LLM results (only if user hasn't touched the field)
        if (
          docQueue.llmDocumentTypeEnumId &&
          !touchedFields.queues?.[queueIndex]?.documentTypeEnumId
        ) {
          setValue(
            `queues.${queueIndex}.documentTypeEnumId`,
            docQueue.llmDocumentTypeEnumId
          );
          setAutoPopulatedFields((prev: any) => [
            ...prev.filter(
              (field: any) =>
                field !== `queues.${queueIndex}.documentTypeEnumId`
            ),
            `queues.${queueIndex}.documentTypeEnumId`,
          ]);
        }

        if (docQueue.llmAsOfDate && !touchedFields.queues?.[queueIndex]?.date) {
          // Convert date to YYYY-MM-DD format
          const dateStr = new Date(docQueue.llmAsOfDate)
            .toISOString()
            .split("T")[0];
          setValue(`queues.${queueIndex}.date`, dateStr);
          setAutoPopulatedFields((prev: any) => [
            ...prev.filter(
              (field: any) => field !== `queues.${queueIndex}.date`
            ),
            `queues.${queueIndex}.date`,
          ]);
        }

        // Update entity selection based on LLM entity ID and type.
        // EntitySelector resolves the name via GraphQL when it sees an id-only value.
        if (docQueue.llmEntityId && docQueue.llmEntityTypeId) {
          const queueTouchedFields = touchedFields.queues?.[queueIndex] as any;
          const entityTypeKey = `queues.${queueIndex}.entities.${docQueue.llmEntityTypeId}` as const;
          const isEntityTouched = queueTouchedFields?.entities;

          if (!isEntityTouched) {
            (setValue as any)(entityTypeKey, [
              { id: docQueue.llmEntityId },
            ], { shouldTouch: true });
            setAutoPopulatedFields((prev: any) => [
              ...prev.filter((field: any) => field !== entityTypeKey),
              entityTypeKey,
            ]);
          }
        }

        // Store LLM notes for potential display
        if (docQueue.llmDocumentTypeEnumNotes) {
          setValue(
            `queues.${queueIndex}.llmNotes`,
            docQueue.llmDocumentTypeEnumNotes
          );
        }

        if (docQueue.llmEntityNotes) {
          setValue(
            `queues.${queueIndex}.llmEntityNotes`,
            docQueue.llmEntityNotes
          );
        }

        if (docQueue.llmEntityTypeNotes) {
          setValue(
            `queues.${queueIndex}.llmEntityTypeNotes`,
            docQueue.llmEntityTypeNotes
          );
        }

        if (docQueue.llmAsOfDateNotes) {
          setValue(
            `queues.${queueIndex}.llmAsOfDateNotes`,
            docQueue.llmAsOfDateNotes
          );
        }

        // Store duplicate documents data
        if (docQueue.duplicateDocuments) {
          setValue(
            `queues.${queueIndex}.duplicateDocuments`,
            docQueue.duplicateDocuments
          );
        }

        // Remove from polling list if LLM processing is complete
        if (docQueue.llmProcessedAt && docQueue.id) {
          setDocumentQueueIds((prev: string[]) =>
            prev.filter((id: string) => id !== docQueue.id)
          );
          // Remove from processing documents set
          setProcessingDocuments((prev: Set<string>) => {
            const newSet = new Set(prev);
            newSet.delete(docQueue.id!);
            return newSet;
          });
        }
      });
    }
  }, [documentQueuesData, queues, setValue, watch, setAutoPopulatedFields]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (stopPolling) {
        stopPolling();
      }
    };
  }, [stopPolling]);

  // Handle file upload and processing
  const handleFileUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      try {
        // Get existing file names for deduplication
        const existingFileNames = new Set(
          queues?.map(queue => queue.file?.name || "") || []
        );

        // Process and filter files
        const processedFiles: File[] = [];
        const skippedFiles: string[] = [];

        // Handle zip files and filter duplicates
        for (const file of files) {
          if (file.name.toLowerCase().endsWith(".zip")) {
            const zip = new JSZip();
            const zipContent = await zip.loadAsync(file);

            const zipFiles = await Promise.all(
              Object.values(zipContent.files)
                // Filter out __MACOSX files and directories
                .filter(
                  zipEntry =>
                    !zipEntry.dir &&
                    !zipEntry.name.includes("__MACOSX/") &&
                    !zipEntry.name.startsWith("._")
                )
                .map(async zipEntry => {
                  const content = await zipEntry.async("blob");
                  return new File([content], zipEntry.name, {
                    type: content.type,
                  });
                })
            );

            // Filter out duplicate files from zip
            for (const zipFile of zipFiles) {
              if (!existingFileNames.has(zipFile.name)) {
                processedFiles.push(zipFile);
                existingFileNames.add(zipFile.name);
              } else {
                skippedFiles.push(zipFile.name);
              }
            }
          } else {
            // Handle regular files
            if (!existingFileNames.has(file.name)) {
              processedFiles.push(file);
              existingFileNames.add(file.name);
            } else {
              skippedFiles.push(file.name);
            }
          }
        }

        // Show notification if files were skipped
        if (skippedFiles.length > 0) {
          setNotification({
            message: `Skipped ${skippedFiles.length} duplicate file(s)`,
            type: "info",
            open: true,
          });

          // If all files were duplicates, exit early
          if (processedFiles.length === 0) {
            return;
          }
        }

        // Create initial queue objects with current override values
        const startIndex = queues?.length || 0;
        const currentOverrideAccessLevel = getValues("overrideAccessLevel");
        const currentOverrideDate = getValues("overrideDate");
        const currentOverrideDocumentTypeEnumId = getValues(
          "overrideDocumentTypeEnumId"
        );
        const currentOverrideEntities = getValues("overrideEntities");

        const newQueues: DocumentQueueFormInput[] = processedFiles.map(
          (file, i) => {
            // Get today's date in YYYY-MM-DD format
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, "0");
            const day = String(today.getDate()).padStart(2, "0");
            const todayDate = `${year}-${month}-${day}`;

            const initialQueue: DocumentQueueFormInput = {
              // Set individual row defaults
              accessLevel: ORGANIZATION_ACCESS_LEVEL,
              date: todayDate,
              entities: {},
              // Apply override values if they exist
              ...(currentOverrideAccessLevel
                ? { accessLevel: currentOverrideAccessLevel }
                : {}),
              ...(currentOverrideDate ? { date: currentOverrideDate } : {}),
              ...(currentOverrideDocumentTypeEnumId
                ? { documentTypeEnumId: currentOverrideDocumentTypeEnumId }
                : {}),
              ...(currentOverrideEntities
                ? { entities: { ...currentOverrideEntities } }
                : {}),
            };

            return initialQueue;
          }
        );

        if (!queues) {
          setValue("queues", [...newQueues]);
        } else {
          setValue("queues", [...queues, ...newQueues]);
        }

        // Initialize uploading state for new files
        const newUploadingFiles = { ...uploadingFiles };
        for (let i = 0; i < processedFiles.length; i++) {
          newUploadingFiles[startIndex + i] = true;
        }
        setUploadingFiles(newUploadingFiles);

        // Get signed URLs for uploads
        const { data: signedUrlData } = await addDocumentQueueSignedURI({
          variables: {
            names: processedFiles.map(file => file.name),
          },
        });

        if (!signedUrlData?.addDocumentQueueSignedURI) {
          throw new Error("Failed to get signed URLs");
        }

        // Process each file
        const queueUrls = signedUrlData.addDocumentQueueSignedURI;
        await Promise.all(
          processedFiles.map(async (file, i) => {
            const queueInfo = queueUrls[i];
            const fileIndex = startIndex + i;

            // Set initial queue info
            setValue(`queues.${fileIndex}.file`, file);
            setValue(`queues.${fileIndex}.title`, file.name);

            try {
              // Upload file to signed URL
              const uploadResponse = await fetch(queueInfo.url as string, {
                method: "PUT",
                body: file,
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
              });

              if (!uploadResponse.ok) {
                throw new Error(
                  `File upload failed: ${uploadResponse.statusText}`
                );
              }

              // Set queue ID and start processing
              setValue(
                `queues.${fileIndex}.documentQueueId`,
                queueInfo.documentQueueId
              );

              // Add to polling list
              setDocumentQueueIds(prev => [...prev, queueInfo.documentQueueId]);

              // Add to processing documents set
              setProcessingDocuments((prev: Set<string>) => {
                const newSet = new Set(prev);
                newSet.add(queueInfo.documentQueueId);
                return newSet;
              });

              // Process queue document to trigger LLM analysis
              await processDocumentQueue({
                variables: { documentQueueId: queueInfo.documentQueueId },
              });
            } catch (error: any) {
              console.error(`Error processing file ${file.name}:`, error);
              setValue(`queues.${fileIndex}.error`, error.message);

              // Remove from processing set on error
              setProcessingDocuments((prev: Set<string>) => {
                const newSet = new Set(prev);
                newSet.delete(queueInfo.documentQueueId);
                return newSet;
              });
            } finally {
              // Mark file as done uploading
              setUploadingFiles(prev => ({
                ...prev,
                [fileIndex]: false,
              }));
            }
          })
        );

        setNotification({
          message: `${processedFiles.length} files uploaded successfully`,
          type: "success",
          open: true,
        });
      } catch (error: any) {
        console.error("File upload error:", error);
        setNotification({
          message: `Error uploading files: ${error.message}`,
          type: "error",
          open: true,
        });

        // Reset uploading state on error
        const newUploadingFiles = { ...uploadingFiles };
        const startIndex = queues?.length || 0;
        for (let i = 0; i < files.length; i++) {
          newUploadingFiles[startIndex + i] = false;
        }
        setUploadingFiles(newUploadingFiles);
      }
    },
    [
      queues,
      setValue,
      watch,
      addDocumentQueueSignedURI,
      processDocumentQueue,
      uploadingFiles,
    ]
  );

  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (
    e: React.DragEvent<HTMLDivElement>
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const files: File[] = [];
      let hasDirectory = false;

      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const entry = e.dataTransfer.items[i].webkitGetAsEntry();
        if (entry && entry.isDirectory) {
          hasDirectory = true;
          break;
        }
      }

      if (hasDirectory) {
        const processEntry = async (entry: any): Promise<void> => {
          if (entry.isFile) {
            const file = await new Promise<File>(resolve => {
              entry.file((file: File) => resolve(file));
            });
            files.push(file);
          } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const entries = await new Promise<any[]>(resolve => {
              reader.readEntries((entries: any[]) => resolve(entries));
            });

            for (const childEntry of entries) {
              await processEntry(childEntry);
            }
          }
        };

        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          const entry = e.dataTransfer.items[i].webkitGetAsEntry();
          if (entry) {
            await processEntry(entry);
          }
        }

        if (files.length > 0) {
          handleFileUpload(files);
        }
      } else {
        const filesArray = Array.from(e.dataTransfer.files);
        handleFileUpload(filesArray);
      }
    } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const currentValues = getValues();
    await onSubmit(currentValues);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      const isFormValid = await trigger();
      const currentErrors = methods.formState.errors;

      // Clear previous publish errors
      data.queues.forEach((_, index) => {
        setValue(`queues.${index}.publishError`, undefined as any);
      });

      // Separate valid and invalid queues based on form validation
      const queueValidation = data.queues.map((queue, index) => {
        const queueErrors = currentErrors.queues?.[index];
        const hasValidationErrors =
          queueErrors && Object.keys(queueErrors).length > 0;

        const hasAllRequiredFields =
          !hasValidationErrors && queue.documentQueueId && !queue.error;

        const missing: string[] = [];
        if (queueErrors) {
          Object.keys(queueErrors).forEach(fieldName => {
            missing.push(fieldName);
          });
        }
        if (!queue.documentQueueId) missing.push("documentQueueId");
        if (queue.error) missing.push(`error: ${queue.error}`);

        return { queue, index, hasAllRequiredFields, missing };
      });

      const validQueues = queueValidation.filter(
        ({ hasAllRequiredFields }) => hasAllRequiredFields
      );
      const invalidQueues = queueValidation.filter(
        ({ hasAllRequiredFields }) => !hasAllRequiredFields
      );

      // Map valid queues to submission format, creating any new tags first
      const queueInputs = await Promise.all(
        validQueues.map(async ({ queue }) => {
          // Extract entities from the multi-select structure
          const entities = extractEntitiesFromFormData(
            queue.entities as any
          );

          if (!authGroup?.id) {
            throw new Error(
              "No document authorization group found for document."
            );
          }

          const tags = queue.tags || [];
          const existingIds = tags
            .filter((t): t is { id: number; name: string; description?: string | null } => typeof t !== "string")
            .map(t => t.id);
          const newTagNames = tags.filter(
            (t): t is string => typeof t === "string"
          );
          const createdIds = await Promise.all(
            newTagNames.map(async name => {
              const { data } = await addKnowledgeBaseTag({
                variables: { input: { name } },
              });
              return data!.addKnowledgeBaseTags.id;
            })
          );

          return {
            documentQueueId: queue.documentQueueId!,
            date: queue.date!,
            documentTypeEnumId: queue.documentTypeEnumId!,
            accessLevel: queue.accessLevel!,
            entities: entities.map(({ businessObjectEnumId, entityId }) => ({
              businessObjectEnumId,
              entityId,
            })),
            allowDuplicateFile: queue.allowDuplicateFile || false,
            documentGroupId: authGroup?.id,
            knowledgeBaseTagIds: [...existingIds, ...createdIds],
          };
        })
      );

      // Handle case where no valid queues exist
      if (queueInputs.length === 0) {
        const errorMessage = `No valid queues to publish. Missing fields: ${invalidQueues
          .map(item => `Queue ${item.index}: ${item.missing.join(", ")}`)
          .join("; ")}`;

        setNotification({
          message: errorMessage,
          type: "error",
          open: true,
        });
        return;
      }

      // Submit valid documents
      const result = await publishDocumentQueue({
        variables: { input: queueInputs },
      });

      if (result.data?.publishDocumentQueues?.length) {
        const publishResults = result.data.publishDocumentQueues;

        // Build a map from documentQueueId to the original form index
        const queueIdToFormIndex = new Map<string, number>();
        validQueues.forEach(({ queue, index }) => {
          if (queue.documentQueueId) {
            queueIdToFormIndex.set(queue.documentQueueId, index);
          }
        });

        // Separate successes and failures from the publish results
        const successes = publishResults.filter(r => r.success);
        const failures = publishResults.filter(r => !r.success);

        // Set publish errors on failed items
        for (const failure of failures) {
          const formIndex = queueIdToFormIndex.get(failure.documentQueueId);
          if (formIndex !== undefined) {
            setValue(`queues.${formIndex}.publishError`, failure.error || "Failed to publish document");
          }
        }

        // Build published docs list from successes
        const newPublishedDocs = successes
          .filter(r => r.documentId != null)
          .map(r => ({
            id: r.documentId!,
            name: r.documentName || "Document",
          }));

        if (newPublishedDocs.length > 0) {
          setPublishedDocuments(prev => [...prev, ...newPublishedDocs]);
        }

        // Build notification message
        const successCount = successes.length;
        const failureCount = failures.length;
        const invalidCount = invalidQueues.length;

        let message = "";
        if (successCount > 0) {
          message += `Successfully published ${successCount} document${successCount !== 1 ? "s" : ""}`;
        }
        if (failureCount > 0) {
          message += `${successCount > 0 ? ". " : ""}${failureCount} document${failureCount !== 1 ? "s" : ""} failed to publish`;
        }
        if (invalidCount > 0) {
          message += `. ${invalidCount} document${invalidCount !== 1 ? "s" : ""} remain${invalidCount === 1 ? "s" : ""} in the form with validation errors.`;

          setTimeout(() => {
            const invalidDetails = invalidQueues
              .map(({ index, queue, missing }) => {
                const fileName = queue.title || queue.file?.name || "Unknown";
                return `${fileName}: missing ${missing.join(", ")}`;
              })
              .join("; ");

            setNotification({
              message: `Validation errors for remaining documents: ${invalidDetails}`,
              type: "info",
              open: true,
            });
          }, 3000);
        }

        setNotification({
          message,
          type: failureCount > 0 && successCount === 0 ? "error" : successCount > 0 && (failureCount > 0 || invalidCount > 0) ? "info" : "success",
          open: true,
        });

        // Determine which form indices to remove (successful publishes only)
        const successQueueIds = new Set(successes.map(r => r.documentQueueId));
        const indicesToRemove = new Set(
          validQueues
            .filter(({ queue }) => queue.documentQueueId && successQueueIds.has(queue.documentQueueId))
            .map(({ index }) => index)
        );

        const remainingQueues = data.queues.filter(
          (_, index) => !indicesToRemove.has(index)
        );

        setValue("queues", remainingQueues);

        setAutoPopulatedFields(prev => {
          const newAutoPopulated: string[] = [];

          remainingQueues.forEach((queue, newIndex) => {
            const oldIndex = data.queues.findIndex(q => q === queue);
            if (oldIndex !== -1) {
              const oldFieldsForThisQueue = prev.filter(field =>
                field.startsWith(`queues.${oldIndex}.`)
              );

              oldFieldsForThisQueue.forEach(field => {
                const fieldName = field.replace(`queues.${oldIndex}.`, "");
                newAutoPopulated.push(`queues.${newIndex}.${fieldName}`);
              });
            }
          });

          return newAutoPopulated;
        });

        setUploadingFiles(prev => {
          const newUploadingFiles: Record<number, boolean> = {};

          remainingQueues.forEach((queue, newIndex) => {
            const oldIndex = data.queues.findIndex(q => q === queue);
            if (oldIndex !== -1 && prev[oldIndex]) {
              newUploadingFiles[newIndex] = prev[oldIndex];
            }
          });

          return newUploadingFiles;
        });

        setProcessingDocuments(prev => {
          const newProcessingSet = new Set<string>();

          remainingQueues.forEach(queue => {
            if (queue.documentQueueId && prev.has(queue.documentQueueId)) {
              newProcessingSet.add(queue.documentQueueId);
            }
          });

          return newProcessingSet;
        });

        if (remainingQueues.length === 0) {
          methods.reset({
            queues: [],
            overrideAccessLevel: undefined,
            overrideDocumentTypeEnumId: undefined,
            overrideDate: undefined,
            overrideEntities: undefined,
          });
          setAutoPopulatedFields([]);
        }
      } else {
        if (result.errors && result.errors.length > 0) {
          const errorMessages = result.errors
            .map(err => err.message)
            .join("; ");
          setNotification({
            message: `GraphQL errors: ${errorMessages}`,
            type: "error",
            open: true,
          });
        } else if (result.data?.publishDocumentQueues === null) {
          setNotification({
            message:
              "Publish failed: No documents were published (null result)",
            type: "error",
            open: true,
          });
        } else if (
          Array.isArray(result.data?.publishDocumentQueues) &&
          result.data.publishDocumentQueues.length === 0
        ) {
          setNotification({
            message:
              "Publish failed: No documents were published (empty result)",
            type: "error",
            open: true,
          });
        } else {
          setNotification({
            message: `Publish failed: Unexpected result structure.`,
            type: "error",
            open: true,
          });
        }
      }
    } catch (error: any) {
      console.error("Publish error:", error);
      setNotification({
        message: `Error publishing documents: ${error.message}`,
        type: "error",
        open: true,
      });
    }
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <FormProvider {...methods}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {publishedDocuments.length > 0 && (
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recently Published Documents
              </Typography>
              <List dense>
                {publishedDocuments.map((doc, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={
                        <Link
                          href={`${getSiriusBaseUrl()}/documents/${doc.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {doc.name}
                        </Link>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPublishedDocuments([])}
                sx={{ mt: 1 }}
              >
                Clear List
              </Button>
            </Paper>
          )}

          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="center" gap={1} mb={2}>
              <Typography variant="h4" gutterBottom>
                Document Upload
              </Typography>
              <Tooltip title="AI-powered document processing">
                <AutoAwesomeIcon color="primary" />
              </Tooltip>
            </Box>

            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={2} justifyContent="center">
                {/* Global Overrides */}
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Box sx={{ width: "75%" }}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardHeader
                        title={
                          <Typography variant="subtitle1" align="center">
                            Set For All Docs
                          </Typography>
                        }
                        action={
                          <IconButton
                            aria-label="expand"
                            size="small"
                            data-testid="toggle-override"
                          >
                            {overrideOpen ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        }
                        onClick={() => setOverrideOpen(!overrideOpen)}
                        sx={{
                          cursor: "pointer",
                          "& .MuiCardHeader-action": {
                            pointerEvents: "none",
                          },
                        }}
                      />
                      <Collapse in={overrideOpen}>
                        <Box sx={{ p: 2 }}>
                          <Grid container spacing={2}>
                            {/* Set Entities for All */}
                            <Grid item xs={12}>
                              <div data-cy="set-entity-for-all">
                                <DocumentEntityInput
                                  allowChange={true}
                                  isOverride={true}
                                  isRequired={false}
                                  autoPopulatedFields={autoPopulatedFields}
                                  setAutoPopulatedFields={
                                    setAutoPopulatedFields
                                  }
                                />
                              </div>
                            </Grid>

                            {/* Set Date for All */}
                            <Grid item xs={4}>
                              <Controller
                                name="overrideDate"
                                control={control}
                                render={({ field, fieldState }) => {
                                  const handleDateChange = (
                                    date: Date | null
                                  ) => {
                                    if (date === null) {
                                      field.onChange(null);
                                    } else if (date && !isNaN(date.getTime())) {
                                      const year = date.getFullYear();
                                      const month = String(
                                        date.getMonth() + 1
                                      ).padStart(2, "0");
                                      const day = String(
                                        date.getDate()
                                      ).padStart(2, "0");
                                      const isoDate = `${year}-${month}-${day}`;
                                      field.onChange(isoDate);
                                    }
                                  };

                                  return (
                                    <DatePicker
                                      label="Set Date for All"
                                      value={getDateValue(field.value ?? null)}
                                      onChange={handleDateChange}
                                      minDate={new Date("1900-01-01")}
                                      slotProps={{
                                        field: { clearable: true },
                                        textField: {
                                          fullWidth: true,
                                          error: !!fieldState.error,
                                          helperText:
                                            fieldState.error?.message || "",
                                        },
                                        actionBar: {
                                          actions: ["clear", "today"],
                                        },
                                      }}
                                    />
                                  );
                                }}
                              />
                            </Grid>

                            {/* Set Access Level for All */}
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>
                                  Set Access Level for All
                                </InputLabel>
                                <Controller
                                  name="overrideAccessLevel"
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      name="overrideAccessLevel"
                                      label="Set Access Level for All"
                                      value={field.value || ""}
                                    >
                                      <MenuItem value="">None</MenuItem>
                                      {accessLevelsData?.documentAccessLevelList?.items?.map(
                                        level => (
                                          <MenuItem
                                            key={level.id}
                                            value={level.id}
                                          >
                                            {level.name}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            {/* Set Document Type for All */}
                            <Grid item xs={4}>
                              <FormControl fullWidth>
                                <InputLabel>
                                  Set Document Type for All
                                </InputLabel>
                                <Controller
                                  name="overrideDocumentTypeEnumId"
                                  control={control}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      name="overrideDocumentTypeEnumId"
                                      label="Set Document Type for All"
                                      value={field.value || ""}
                                    >
                                      <MenuItem value="">None</MenuItem>
                                      {documentTypesData?.documentTypeEnumList?.items?.map(
                                        type => (
                                          <MenuItem
                                            key={type.id}
                                            value={type.id}
                                          >
                                            {type.name}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </Card>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* File Upload Area */}
              <Box sx={{ my: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Upload Files or Folders
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    border: "2px dashed",
                    borderColor: isDragging ? "primary.main" : "grey.400",
                    borderRadius: 2,
                    minHeight: "150px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDragging
                      ? "rgba(0, 0, 0, 0.05)"
                      : "transparent",
                    transition: "all 0.3s ease",
                  }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    style={{ display: "none" }}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleFileInputChange}
                  />
                  <label htmlFor="file-upload">
                    <Button component="span" variant="contained" sx={{ mx: 1 }}>
                      Select Files
                    </Button>
                  </label>

                  <br />

                  <input
                    style={{ display: "none" }}
                    id="folder-upload"
                    type="file"
                    // @ts-ignore: Non-standard attribute for folder selection
                    webkitdirectory="true"
                    // @ts-ignore: Non-standard attribute for folder selection
                    directory="true"
                    multiple
                    onChange={handleFileInputChange}
                  />
                  <label htmlFor="folder-upload">
                    <Button component="span" variant="contained" sx={{ mx: 1 }}>
                      Select Folders
                    </Button>
                  </label>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 2 }}
                  >
                    Drag and drop files or folders here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ZIP supported (max 50MB)
                  </Typography>
                </Paper>
              </Box>

              {/* Document Forms */}
              {fields.length > 0 && (
                <Box sx={{ my: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Document Details
                  </Typography>
                  {fields.map((field, index) => (
                    <DocumentRowForm
                      key={field.id}
                      index={index}
                      control={control}
                      setValue={setValue}
                      resetField={resetField}
                      errors={errors}
                      watch={watch}
                      onRemove={id => {
                        const currentValues = getValues();
                        const newQueues = currentValues.queues.filter(
                          (queue: any, index: number) => index !== id
                        );
                        setValue("queues", newQueues, {
                          shouldDirty: true,
                          shouldTouch: true,
                        });

                        setUploadingFiles(prev => {
                          const newUploading: Record<number, boolean> = {};
                          Object.entries(prev).forEach(([key, value]) => {
                            const keyNum = parseInt(key);
                            if (keyNum < id) {
                              newUploading[keyNum] = value;
                            } else if (keyNum > id) {
                              newUploading[keyNum - 1] = value;
                            }
                          });
                          return newUploading;
                        });

                        setAutoPopulatedFields(prev => {
                          return prev
                            .filter(field => !field.startsWith(`queues.${id}.`))
                            .map(field => {
                              const match = field.match(
                                /^queues\.(\d+)\.(.+)$/
                              );
                              if (match) {
                                const fieldIndex = parseInt(match[1]);
                                const fieldName = match[2];
                                if (fieldIndex > id) {
                                  return `queues.${
                                    fieldIndex - 1
                                  }.${fieldName}`;
                                }
                              }
                              return field;
                            });
                        });

                        const removedQueue = currentValues.queues[id];
                        if (removedQueue?.documentQueueId) {
                          deleteDocumentQueue({
                            variables: { id: removedQueue.documentQueueId },
                          }).catch((error: any) => {
                            console.error(
                              "Error deleting document queue:",
                              error
                            );
                          });

                          setProcessingDocuments((prev: Set<string>) => {
                            const newSet = new Set(prev);
                            newSet.delete(
                              removedQueue.documentQueueId as string
                            );
                            return newSet;
                          });
                        }
                      }}
                      isUploading={uploadingFiles[index]}
                      autoPopulatedFields={autoPopulatedFields}
                      setAutoPopulatedFields={setAutoPopulatedFields}
                      isAiProcessing={(() => {
                        const queue = getValues(`queues.${index}`);
                        return queue?.documentQueueId
                          ? processingDocuments.has(queue.documentQueueId)
                          : false;
                      })()}
                    />
                  ))}
                </Box>
              )}

              {/* Submit Button */}
              {fields.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  {/* Validation Errors Display */}
                  {(() => {
                    const validationErrors = getValidationErrors();
                    return validationErrors.length > 0 ? (
                      <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        icon={<ErrorOutlineIcon />}
                      >
                        <AlertTitle>Validation Errors</AlertTitle>
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {validationErrors.slice(0, 5).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {validationErrors.length > 5 && (
                            <li>
                              ... and {validationErrors.length - 5} more errors
                            </li>
                          )}
                        </Box>
                      </Alert>
                    ) : null;
                  })()}

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Select
                      value={authGroup?.id || null}
                      onChange={e => {
                        e.preventDefault();
                        const selectedGroup =
                          documentAuthGroupsData?.userList.items.find(
                            group => group.id === e.target.value
                          );
                        setAuthGroup(selectedGroup || null);
                      }}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                    >
                      {(documentAuthGroupsData?.userList.items || []).map(
                        group => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.commonName}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<SendIcon />}
                    >
                      Publish Documents
                    </Button>
                  </Box>
                </Box>
              )}
            </form>
          </Paper>
        </Container>

        {/* Notifications */}
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.type}>
            {notification.message}
          </Alert>
        </Snackbar>
      </LocalizationProvider>
    </FormProvider>
  );
};

export default MultiDocumentUpload;
